import { ThumbsTooltipButton } from '@/components/ui/csat-feedback/binary-feedback/thumbsTooltipButton';
import { DictationCsatPopover } from '@/features/dictation/dictationBubble/csat/DictationCsatPopover';
import { resolveDictationContext } from '@/features/dictation/dictationBubble/store/getDictationContext';
import { useDictationStore } from '@/features/dictation/dictationBubble/store/useDictationStore';
import { DictationError } from '@/features/dictation/dictationBubble/ui/errors/DictationError';
import { DictationInsertionError } from '@/features/dictation/dictationBubble/ui/errors/DictationInsertionError';
import { useDesktopDictationHotkey } from '@/features/dictation/dictationBubble/ui/hooks/useDesktopDictationHotkey';
import { useInvalidateDictationContextOnSessionCreate } from '@/features/dictation/dictationBubble/ui/hooks/useInvalidateDictationContextOnSessionCreate';
import { useWebDictationHotkey } from '@/features/dictation/dictationBubble/ui/hooks/useWebDictationHotkey';
import { DictationListening } from '@/features/dictation/dictationBubble/ui/listening/DictationListening';
import { useMaxDuration } from '@/features/dictation/dictationBubble/ui/listening/hooks/useMaxDuration';
import { DictationMicSelector } from '@/features/dictation/dictationBubble/ui/micSelector/DictationMicSelector';
import { DictationProcessing } from '@/features/dictation/dictationBubble/ui/processing/DictationProcessing';
import { useQueryUserSnippets } from '@/hooks/query/useQueryUserSnippets';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { isProductFeedbackAvailable } from '@/utils/heidiReach';
import {
  DICTATION_BUBBLE_Z_INDEX_CLASS,
  DictationFeedbackConfirmation,
  dictationFeedbackPromptCooldownAtom,
  DictationFeedbackReasonPicker,
  FEEDBACK_CONFIRMATION_AUTO_HIDE_MS,
  FEEDBACK_PROMPT_AUTO_HIDE_MS,
  useDictationFeedbackReasonFlow,
  useDictationFeedbackSubmission,
  useDictationFeedbackSubmitHandler,
  useWidgetAutoHide,
  WidgetOutputFeedback,
} from '@heidi/dictate';
import { DICTATION_FEEDBACK_HISTORY_HREF } from '@heidi/dictate/contracts';
import { isTauri } from '@heidi/runtime/platform';
import { useSetAtom } from 'jotai';
import { AnimatePresence } from 'motion/react';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useMemo } from 'react';

const noop = () => {};

export const DictationBubble = () => {
  const router = useRouter();
  const cancel = useDictationStore((s) => s.cancel);
  const reset = useDictationStore((s) => s.reset);
  const status = useDictationStore((s) => s.status);
  const errorCode = useDictationStore((s) => s.errorCode);
  const source = useDictationStore((s) => s.source);
  const dictationId = useDictationStore((s) => s.dictationId);
  const onSubmitFeedback = useDictationFeedbackSubmitHandler();
  const startFeedbackCooldown = useSetAtom(dictationFeedbackPromptCooldownAtom);

  const isMobile = useIsMobile();
  const { isReasonPickerOpen, requestNegativeFeedback, closeReasonPicker } =
    useDictationFeedbackReasonFlow({
      active: status === 'FEEDBACK',
    });
  const {
    status: feedbackSubmissionStatus,
    isSubmitting: isFeedbackSubmitting,
    submitFeedback,
    reset: resetFeedbackSubmission,
  } = useDictationFeedbackSubmission({
    active: status === 'FEEDBACK',
    onSubmitFeedback,
    onSuccess: () => {
      startFeedbackCooldown();
      closeReasonPicker();
    },
  });
  const showFeedbackConfirmation =
    status === 'FEEDBACK' && feedbackSubmissionStatus === 'success';
  const showFeedbackReasonPicker =
    status === 'FEEDBACK' && !showFeedbackConfirmation && isReasonPickerOpen;
  const feedbackPositionClassName = cn(
    'fixed inset-x-0 mx-auto',
    DICTATION_BUBBLE_Z_INDEX_CLASS,
    isMobile ? 'bottom-32' : 'bottom-6'
  );
  const feedbackPromptClassName = cn(feedbackPositionClassName, 'w-fit');

  const dismissFeedback = useCallback(() => {
    resetFeedbackSubmission();
    reset();
  }, [reset, resetFeedbackSubmission]);

  const seeFeedbackHistory = useCallback(() => {
    void router
      .push(DICTATION_FEEDBACK_HISTORY_HREF)
      .then(dismissFeedback)
      .catch(() => undefined);
  }, [dismissFeedback, router]);

  // Reuse the desktop widget's hover-pause + auto-dismiss for the FEEDBACK
  // prompt: map FEEDBACK to RESULT so the timer arms, overriding the window to
  // 15s. Any other status maps to INACTIVE, which arms no timer.
  const { onMouseEnter, onMouseMove, onMouseLeave, hovered } = useWidgetAutoHide({
    state: status === 'FEEDBACK' ? 'RESULT' : 'INACTIVE',
    onHide: dismissFeedback,
    suppressAutoHide: showFeedbackReasonPicker || isFeedbackSubmitting,
    autoHideMsOverride:
      status !== 'FEEDBACK'
        ? undefined
        : showFeedbackConfirmation
          ? FEEDBACK_CONFIRMATION_AUTO_HIDE_MS
          : FEEDBACK_PROMPT_AUTO_HIDE_MS,
  });

  useWebDictationHotkey();
  useDesktopDictationHotkey();
  // Web side; desktop mounts the same hook from useDesktopDictationHotkey.
  useInvalidateDictationContextOnSessionCreate(!isTauri());
  // Warm the snippets cache for the last-chunk user_context (web + desktop).
  useQueryUserSnippets();
  // Auto-stops dictation at MAX_DICTATION_DURATION_MS. Mounted at the bubble
  // level (not inside DictationListening) so it also fires for sources whose
  // listening UI is suppressed — e.g. webDictateButton, where DictationBubble
  // returns null on LISTENING/PROCESSING and the bubble tree never renders.
  useMaxDuration();

  useEffect(() => {
    if (isTauri()) return;
    // Fire-and-forget warm-up so first dictation avoids a cold cache miss.
    void resolveDictationContext();
  }, []);

  useEffect(() => {
    if (status === 'INACTIVE') return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (status === 'ERROR' || status === 'INSERTION_ERROR' || status === 'FEEDBACK') {
          reset();
        } else {
          cancel();
        }
      }
    };

    globalThis.addEventListener('keydown', handleEscape);
    return () => globalThis.removeEventListener('keydown', handleEscape);
  }, [status, cancel, reset]);

  const content = useMemo(() => {
    switch (status) {
      case 'INSERTION_ERROR':
        return <DictationInsertionError key="insertion-error" />;
      case 'ERROR':
        return errorCode === 'COULD_NOT_HEAR_LONG' ? (
          <DictationMicSelector key="mic-selector" />
        ) : (
          <DictationError key="error" />
        );
      case 'LISTENING':
        return source === 'webDictateButton' ? null : (
          <DictationListening key="listening" />
        );
      case 'PROCESSING':
        return source === 'webDictateButton' ? null : (
          <DictationProcessing key="processing" />
        );
      case 'FEEDBACK':
        // The store only enters FEEDBACK for sources that render the bubble
        // (webDictateButton never reaches here), so no source guard is needed.
        if (showFeedbackConfirmation) {
          return (
            <DictationFeedbackConfirmation
              key="feedback-confirmation"
              className={feedbackPositionClassName}
              onDismiss={dismissFeedback}
              onSeeHistory={seeFeedbackHistory}
              surface="web_bubble"
              onMouseEnter={onMouseEnter}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
            />
          );
        }
        if (showFeedbackReasonPicker) {
          return (
            <DictationFeedbackReasonPicker
              key="feedback-reasons"
              className={feedbackPositionClassName}
              dictationId={dictationId}
              isSubmitting={isFeedbackSubmitting}
              platform="web"
              onCancel={dismissFeedback}
              onSubmit={submitFeedback}
              onMouseEnter={onMouseEnter}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
            />
          );
        }
        return (
          <WidgetOutputFeedback
            key="feedback"
            // Web has no floating widget window, so the caller positions the
            // bubble bottom-center like the other web states.
            className={feedbackPromptClassName}
            closeVisible={hovered}
            dictationId={dictationId}
            isCopied={false}
            isSubmitting={isFeedbackSubmitting}
            showFeedback
            platform="web"
            onClose={dismissFeedback}
            onCopy={noop}
            onMouseEnter={onMouseEnter}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onSubmitFeedback={submitFeedback}
            onRequestNegativeFeedback={requestNegativeFeedback}
            renderThumbsButton={(props) => <ThumbsTooltipButton {...props} />}
          />
        );
      default:
        return null;
    }
  }, [
    status,
    errorCode,
    source,
    hovered,
    onMouseEnter,
    onMouseMove,
    onMouseLeave,
    dictationId,
    showFeedbackReasonPicker,
    isFeedbackSubmitting,
    showFeedbackConfirmation,
    submitFeedback,
    dismissFeedback,
    seeFeedbackHistory,
    requestNegativeFeedback,
    feedbackPositionClassName,
    feedbackPromptClassName,
  ]);

  return (
    <>
      <AnimatePresence>{content}</AnimatePresence>
      {isProductFeedbackAvailable && <DictationCsatPopover />}
    </>
  );
};
